import { TRACKING_EXPIRY_MS, TRACKING_KEYS } from './constants';
import { UserContext, TrackingData } from '@peptalk/shared';

declare const navigator: Navigator;

/**
 * A single atomic last-touch attribution record. `params` holds only the
 * attribution parameters that were present on the visit that captured it,
 * using the same camelCase field names as TrackingData.
 */
interface TrackingRecord {
  capturedAt: number;
  params: Record<string, string>;
}

/**
 * Attribution URL parameters, in the exact same spellings the previous
 * implementation accepted (lowercase and uppercase variants).
 */
const ATTRIBUTION_PARAMS: ReadonlyArray<{
  key: keyof TrackingData;
  names: readonly string[];
}> = [
  { key: 'impactCode', names: ['rsCode', 'impact_code', 'impactCode'] },
  { key: 'gclid', names: ['gclid', 'GCLID'] },
  { key: 'msclkid', names: ['msclkid', 'MSCLKID'] },
  { key: 'gbraid', names: ['gbraid', 'GBRAID'] },
  { key: 'wbraid', names: ['wbraid', 'WBRAID'] },
  { key: 'fbclid', names: ['fbclid', 'FBCLID'] },
  { key: 'liFatId', names: ['li_fat_id', 'LI_FAT_ID'] },
  { key: 'rdtCid', names: ['rdt_cid', 'RDT_CID'] },
  { key: 'dclid', names: ['dclid', 'DCLID'] },
  { key: 'utmSource', names: ['utm_source'] },
  { key: 'utmMedium', names: ['utm_medium'] },
  { key: 'utmCampaign', names: ['utm_campaign'] },
  { key: 'utmTerm', names: ['utm_term'] },
  { key: 'utmContent', names: ['utm_content'] },
];

/**
 * Collect every attribution parameter present in the URL query string with a
 * non-empty value, using the same camelCase field names as TrackingData.
 */
function collectAttributionParams(
  urlParams: URLSearchParams
): Record<string, string> {
  const params: Record<string, string> = {};
  for (const { key, names } of ATTRIBUTION_PARAMS) {
    for (const name of names) {
      const value = urlParams.get(name);
      if (value) {
        params[key] = value;
        break;
      }
    }
  }
  return params;
}

/**
 * Read and validate the stored atomic record, returning null when it is
 * absent or malformed.
 */
function readTrackingRecord(): TrackingRecord | null {
  const rawRecord = localStorage.getItem(TRACKING_KEYS.ATTRIBUTION_RECORD);
  if (!rawRecord) {
    return null;
  }
  const record = JSON.parse(rawRecord) as TrackingRecord;
  if (
    !record ||
    typeof record.capturedAt !== 'number' ||
    typeof record.params !== 'object' ||
    record.params === null ||
    Array.isArray(record.params)
  ) {
    return null;
  }
  return record;
}

/**
 * Build TrackingData from the record's params, keeping only non-empty values.
 */
function recordToTrackingData(record: TrackingRecord): TrackingData {
  const trackingData: TrackingData = {};
  for (const key of Object.keys(record.params)) {
    const value = record.params[key];
    if (value) {
      trackingData[key as keyof TrackingData] = value;
    }
  }
  return trackingData;
}

/**
 * One-time legacy migration: read the old individual keys as if they had just
 * been captured, so existing visitors keep their stored attribution.
 */
function readLegacyTrackingData(): TrackingData {
  const trackingData: TrackingData = {};

  const impactCode = localStorage.getItem(TRACKING_KEYS.IMPACT_CODE);
  if (impactCode) {
    trackingData.impactCode = impactCode;
  }

  const gclid = localStorage.getItem(TRACKING_KEYS.GCLID_CODE);
  if (gclid) {
    trackingData.gclid = gclid;
  }

  const msclkid = localStorage.getItem(TRACKING_KEYS.MSCLKID_CODE);
  if (msclkid) {
    trackingData.msclkid = msclkid;
  }

  const gbraid = localStorage.getItem(TRACKING_KEYS.GBRAID_CODE);
  if (gbraid) {
    trackingData.gbraid = gbraid;
  }

  const wbraid = localStorage.getItem(TRACKING_KEYS.WBRAID_CODE);
  if (wbraid) {
    trackingData.wbraid = wbraid;
  }

  const fbclid = localStorage.getItem(TRACKING_KEYS.FBCLID_CODE);
  if (fbclid) {
    trackingData.fbclid = fbclid;
  }

  const liFatId = localStorage.getItem(TRACKING_KEYS.LI_FAT_ID_CODE);
  if (liFatId) {
    trackingData.liFatId = liFatId;
  }

  const rdtCid = localStorage.getItem(TRACKING_KEYS.RDT_CID_CODE);
  if (rdtCid) {
    trackingData.rdtCid = rdtCid;
  }

  const dclid = localStorage.getItem(TRACKING_KEYS.DCLID_CODE);
  if (dclid) {
    trackingData.dclid = dclid;
  }

  const utmSource = localStorage.getItem(TRACKING_KEYS.UTM_SOURCE);
  if (utmSource) {
    trackingData.utmSource = utmSource;
  }

  const utmMedium = localStorage.getItem(TRACKING_KEYS.UTM_MEDIUM);
  if (utmMedium) {
    trackingData.utmMedium = utmMedium;
  }

  const utmCampaign = localStorage.getItem(TRACKING_KEYS.UTM_CAMPAIGN);
  if (utmCampaign) {
    trackingData.utmCampaign = utmCampaign;
  }

  const utmTerm = localStorage.getItem(TRACKING_KEYS.UTM_TERM);
  if (utmTerm) {
    trackingData.utmTerm = utmTerm;
  }

  const utmContent = localStorage.getItem(TRACKING_KEYS.UTM_CONTENT);
  if (utmContent) {
    trackingData.utmContent = utmContent;
  }

  return trackingData;
}

/**
 * Initialize tracking parameters from URL and store them in localStorage
 * Similar to the provided Intercom tracking script
 *
 * A qualifying visit (one carrying at least one attribution parameter)
 * replaces the single atomic last-touch record. An untagged visit leaves
 * localStorage untouched, so attribution can never mix parameters captured
 * on different visits.
 */
export function initializeTracking() {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const params = collectAttributionParams(urlParams);

    // Not a qualifying visit: never overwrite stored attribution or reset the
    // expiry clock.
    if (Object.keys(params).length === 0) {
      return;
    }

    const record: TrackingRecord = {
      capturedAt: Date.now(),
      params,
    };
    localStorage.setItem(
      TRACKING_KEYS.ATTRIBUTION_RECORD,
      JSON.stringify(record)
    );
  } catch (error) {
    console.warn('Failed to initialize tracking parameters:', error);
  }
}

/**
 * Remove the legacy individual tracking keys. Called when an expired atomic
 * record is encountered so stale, never-expiring legacy values can never be
 * returned by a later call.
 */
function removeLegacyTrackingData(): void {
  localStorage.removeItem(TRACKING_KEYS.IMPACT_CODE);
  localStorage.removeItem(TRACKING_KEYS.GCLID_CODE);
  localStorage.removeItem(TRACKING_KEYS.MSCLKID_CODE);
  localStorage.removeItem(TRACKING_KEYS.GBRAID_CODE);
  localStorage.removeItem(TRACKING_KEYS.WBRAID_CODE);
  localStorage.removeItem(TRACKING_KEYS.FBCLID_CODE);
  localStorage.removeItem(TRACKING_KEYS.LI_FAT_ID_CODE);
  localStorage.removeItem(TRACKING_KEYS.RDT_CID_CODE);
  localStorage.removeItem(TRACKING_KEYS.DCLID_CODE);
  localStorage.removeItem(TRACKING_KEYS.UTM_SOURCE);
  localStorage.removeItem(TRACKING_KEYS.UTM_MEDIUM);
  localStorage.removeItem(TRACKING_KEYS.UTM_CAMPAIGN);
  localStorage.removeItem(TRACKING_KEYS.UTM_TERM);
  localStorage.removeItem(TRACKING_KEYS.UTM_CONTENT);
}

/**
 * Get stored tracking data for including in session/contact information
 */
export function getTrackingData(): TrackingData {
  let record: TrackingRecord | null = null;
  try {
    record = readTrackingRecord();
  } catch (error) {
    console.warn('Failed to read tracking record:', error);
  }

  // A present record is authoritative: honour the 90-day expiry window and
  // never fall back to the never-expiring legacy keys.
  if (record) {
    if (Date.now() - record.capturedAt <= TRACKING_EXPIRY_MS) {
      return recordToTrackingData(record);
    }
    // Expired: clear the legacy individual keys so stale values can never be
    // returned by a later call, then return no attribution.
    try {
      removeLegacyTrackingData();
    } catch (error) {
      console.warn('Failed to clear expired tracking data:', error);
    }
    return {};
  }

  // No record at all (absent or unparseable): one-time legacy migration,
  // treating the old individual keys as if they had just been captured.
  try {
    return readLegacyTrackingData();
  } catch (error) {
    console.warn('Failed to read legacy tracking data:', error);
    return {};
  }
}

/**
 * Clear all stored tracking data from localStorage
 * This should be called on form submission to reset tracking parameters
 */
export function clearTrackingData(): void {
  try {
    // Clear all tracking parameters from localStorage
    localStorage.removeItem(TRACKING_KEYS.ATTRIBUTION_RECORD);
    localStorage.removeItem(TRACKING_KEYS.IMPACT_CODE);
    localStorage.removeItem(TRACKING_KEYS.GCLID_CODE);
    localStorage.removeItem(TRACKING_KEYS.MSCLKID_CODE);
    localStorage.removeItem(TRACKING_KEYS.GBRAID_CODE);
    localStorage.removeItem(TRACKING_KEYS.WBRAID_CODE);
    localStorage.removeItem(TRACKING_KEYS.FBCLID_CODE);
    localStorage.removeItem(TRACKING_KEYS.LI_FAT_ID_CODE);
    localStorage.removeItem(TRACKING_KEYS.RDT_CID_CODE);
    localStorage.removeItem(TRACKING_KEYS.DCLID_CODE);
    localStorage.removeItem(TRACKING_KEYS.UTM_SOURCE);
    localStorage.removeItem(TRACKING_KEYS.UTM_MEDIUM);
    localStorage.removeItem(TRACKING_KEYS.UTM_CAMPAIGN);
    localStorage.removeItem(TRACKING_KEYS.UTM_TERM);
    localStorage.removeItem(TRACKING_KEYS.UTM_CONTENT);
  } catch (error) {
    console.warn('[Widget] Failed to clear tracking data:', error);
  }
}

/**
 * Get user context information including timezone and browser details
 */
export function getUserContext(): UserContext {
  const context: UserContext = {};

  // TEST MODE: Check for test overrides (used by location emulator on test pages)
  const testConfig =
    typeof window !== 'undefined' ? window.__PEPTALK_TEST__ : undefined;

  try {
    // Get timezone information (use test override if available)
    const timezone =
      testConfig?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (timezone) {
      context.timezone = timezone;
    }

    // Get timezone offset in minutes from UTC (use test override if available)
    context.timezoneOffset =
      testConfig?.timezoneOffset ?? new Date().getTimezoneOffset();

    // Get user language
    const language =
      navigator.language ||
      (navigator as { userLanguage?: string }).userLanguage;
    if (language) {
      context.language = language;
    }

    // Get user agent
    if (navigator.userAgent) {
      context.userAgent = navigator.userAgent;
    }

    // Get referrer
    if (document.referrer) {
      context.referrer = document.referrer;
    }

    // Get current page URL
    if (window.location.href) {
      context.pageUrl = window.location.href;
    }
  } catch (error) {
    console.warn('Failed to get user context:', error);
  }

  return context;
}
